import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, importProvidersFrom } from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { AuthModule, getAuth, provideAuth } from "@angular/fire/auth";
import { ReactiveFormsModule } from "@angular/forms";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import {
  BrowserAnimationsModule,
  provideAnimations,
  provideNoopAnimations,
} from "@angular/platform-browser/animations";
import { RouterModule, UrlSerializer } from "@angular/router";
import { environment } from "@env";
import {
  TRANSLOCO_LOADER,
  TranslocoService,
  provideTransloco,
  translocoConfig,
} from "@ngneat/transloco";
import { provideEffects } from "@ngrx/effects";
import { provideStore } from "@ngrx/store";
import {
  ChallengesEffects,
  CountriesEffects,
  UserEffects,
  challengesReducer,
  countriesReducer,
  userReducer,
} from "@store";
import { IntercomModule } from "@supy-io/ngx-intercom";
import { RECAPTCHA_SETTINGS, RecaptchaFormsModule, RecaptchaModule } from "ng-recaptcha";
import { ToastrModule } from "ngx-toastr";
import { register } from "swiper/element/bundle";
import { AppComponent } from "./app/app.component";
import { routes } from "./app/app.routes";
import { ApiInterceptor, ErrorInterceptor } from "./app/core/interceptor";
import { LocalizedUrlSerializer } from "./app/core/router/localized-url-serializer";
import { TRANSLOCO_CONFIG, TranslocoHttpLoader, loadTranslations } from "./app/transloco.config";

function setAffiliateId(affiliateId: string) {
  sessionStorage.setItem("affiliateId", affiliateId);
}

export function handleReferralRedirect(): boolean {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const urlParams = new URLSearchParams(window.location.search);
  const affiliateIdFromQuery = urlParams.get("affiliateId");

  if (affiliateIdFromQuery) {
    setAffiliateId(affiliateIdFromQuery);

    const url = new URL(window.location.href);
    url.searchParams.delete("affiliateId");
    window.history.replaceState({}, "", url.toString());

    return true;
  }

  if (pathParts.length === 3 && ["en", "de", "fr"].includes(pathParts[0]) && pathParts[1] === "a") {
    setAffiliateId(pathParts[2]);
    window.location.replace(`/${pathParts[0]}`);
    return false;
  }

  if (pathParts.length === 2 && pathParts[0] === "a") {
    setAffiliateId(pathParts[1]);
    window.location.replace("/en");
    return false;
  }

  return true;
}

register();

if (handleReferralRedirect()) {
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        BrowserAnimationsModule,
        RecaptchaModule,
        RecaptchaFormsModule,
        BrowserModule,
        ReactiveFormsModule,
        RouterModule.forRoot(routes, { scrollPositionRestoration: "enabled" }),
        ToastrModule.forRoot(),
        IntercomModule.forRoot({ appId: environment.intercomAppId, updateOnRouterChange: true }),
        AuthModule,
        provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
        provideAuth(() => getAuth()),
      ),
      { provide: UrlSerializer, useClass: LocalizedUrlSerializer },
      provideHttpClient(withInterceptorsFromDi()),
      provideStore({
        user: userReducer,
        challenges: challengesReducer,
        countries: countriesReducer,
      }),
      provideEffects(ChallengesEffects, UserEffects, CountriesEffects),
      provideTransloco({ config: translocoConfig(TRANSLOCO_CONFIG) }),
      provideNoopAnimations(),
      provideAnimations(),
      {
        provide: APP_INITIALIZER,
        multi: true,
        deps: [TranslocoService],
        useFactory: loadTranslations,
      },
      { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ApiInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
      },
      {
        provide: RECAPTCHA_SETTINGS,
        useValue: {
          siteKey: environment.recaptchaSiteKey,
        },
      },
    ],
    // eslint-disable-next-line no-console
  }).catch((err) => console.error(err));
}
